import { supabase } from '@/core/database/supabase';
import type { ConstructionQaInspection, QaFilters } from '../types/construction-qa.types';

export const ConstructionQaInspectionsService = {
  async getInspections(companyId: string, filters: QaFilters = {}): Promise<{ data: ConstructionQaInspection[], total: number }> {
    let query = supabase
      .from('construction_qa_inspections')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, name),
        inspector:user_profiles!inspected_by(id, full_name, avatar_url:avatar_path),
        work_order:work_orders(id, work_order_code, title)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.result && filters.result !== 'all') query = query.eq('result_code', filters.result);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.inspectorId) query = query.eq('inspected_by', filters.inspectorId);
    if (filters.search) {
      query = query.or(`inspection_code.ilike.%${filters.search}%,notes.ilike.%${filters.search}%`);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error, count } = await query;
    if (error) throw error;

    return {
      data: (data || []) as ConstructionQaInspection[],
      total: count || 0
    };
  },

  async getInspectionDetail(id: string): Promise<ConstructionQaInspection> {
    const { data, error } = await supabase
      .from('construction_qa_inspections')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, name),
        inspector:user_profiles!inspected_by(id, full_name, avatar_url:avatar_path),
        work_order:work_orders(id, work_order_code, title)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as ConstructionQaInspection;
  },

  async createInspection(data: Partial<ConstructionQaInspection>): Promise<ConstructionQaInspection> {
    const { data: result, error } = await supabase
      .from('construction_qa_inspections')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as ConstructionQaInspection;
  },

  async updateInspection(id: string, updates: Partial<ConstructionQaInspection>): Promise<void> {
    const { error } = await supabase
      .from('construction_qa_inspections')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  },

  async deleteInspection(id: string): Promise<void> {
    const { error } = await supabase
      .from('construction_qa_inspections')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};
